// ─────────────────────────────────────────────────────────────────────────────
//  MapModal.jsx
//  Full-screen dialog with Google Map, reverse geocoded address, custom marker
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { CircularProgress, Tooltip } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

import {
  ALERT_CONFIGS, ALERT_BORDER,
  speedIcon, ignitionOnIcon, ignitionOffIcon, theftIcon,
  temperingIcon, alertsIcon, geofenceInIcon, geofenceOutIcon, notificationIcon,
} from "./notificationConstants";

const ICON_SRC_MAP = {
  OVER_SPEED_ALERT: speedIcon, IGNITION_ON: ignitionOnIcon,
  IGNITION_OFF: ignitionOffIcon, OUT_ALERT: theftIcon,
  TAMPERING_ALERT: temperingIcon, SOS: alertsIcon,
  USER_IN_ALERT: geofenceInIcon, USER_OUT_ALERT: geofenceOutIcon,
  DEVICE_FUEL_REFILL: notificationIcon, DEVICE_FUEL_DROP: notificationIcon,
  default: notificationIcon,
  LOCK: null
};

const MapModal = ({ open, onClose, lat, lng, label, notificationType, tooltipClasses, alertTime, customIconSrc }) => {
  const [customIcon, setCustomIcon] = useState(null);
  const [address, setAddress] = useState("");
  const [copied, setCopied] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: JSON.parse(localStorage.getItem("config"))?.googleMapKey || "",
  });

  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);
  const hasValidCoords = !isNaN(parsedLat) && !isNaN(parsedLng);
  const center = hasValidCoords ? { lat: parsedLat, lng: parsedLng } : { lat: 28.6139, lng: 77.209 };

  const alertConfig = ALERT_CONFIGS[notificationType] || ALERT_CONFIGS.default;
  const borderColor = ALERT_BORDER[notificationType] || ALERT_BORDER.default;
  const [alertTitle, vehiclePart] = (label || "Alert Location").split(" — ");



  /* ── Reverse geocode ── */
  useEffect(() => {
    if (!isLoaded || !hasValidCoords) return;
    const fetchAddress = async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${parsedLat}&lon=${parsedLng}&format=json`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await response.json();
        if (data) {
          setAddress(data?.display_name || `${parsedLat}, ${parsedLng}`);
        }
        else setAddress(`${parsedLat}, ${parsedLng}`);
      } catch (err) {

      }
    };
    fetchAddress();
  }, [isLoaded, parsedLat, parsedLng]);

  /* ── Build canvas pin icon ── */
  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;
    setCustomIcon(null);

    // const iconSrc = ICON_SRC_MAP[notificationType] || ICON_SRC_MAP.default;
    const iconSrc = customIconSrc || ICON_SRC_MAP[notificationType] || ICON_SRC_MAP.default;
    const SIZE = 56, ICON = 28, PIN_TIP = 10;
    const canvas = document.createElement("canvas");
    canvas.width = SIZE; canvas.height = SIZE + PIN_TIP;
    const ctx = canvas.getContext("2d");

    const drawBase = () => {
      ctx.beginPath(); ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff"; ctx.fill();
      ctx.beginPath(); ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 3, 0, Math.PI * 2);
      ctx.fillStyle = borderColor; ctx.fill();
      ctx.beginPath();
      ctx.moveTo(SIZE / 2 - 7, SIZE - 3);
      ctx.lineTo(SIZE / 2 + 7, SIZE - 3);
      ctx.lineTo(SIZE / 2, SIZE + PIN_TIP - 1);
      ctx.closePath(); ctx.fillStyle = borderColor; ctx.fill();
    };
    const makeIcon = () => ({
      url: canvas.toDataURL(),
      scaledSize: new window.google.maps.Size(SIZE, SIZE + PIN_TIP),
      anchor: new window.google.maps.Point(SIZE / 2, SIZE + PIN_TIP - 1),
    });

    drawBase();
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => {
      const off = (SIZE - ICON) / 2;
      ctx.drawImage(img, off, off, ICON, ICON);
      ctx.globalCompositeOperation = "screen";
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      ctx.beginPath(); ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 3, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = "multiply";
      ctx.drawImage(img, off, off, ICON, ICON);
      ctx.globalCompositeOperation = "source-over";
      setCustomIcon(makeIcon());
    };
    img.onerror = () => { ctx.globalCompositeOperation = "source-over"; setCustomIcon(makeIcon()); };
    img.src = iconSrc;
  }, [isLoaded, notificationType, borderColor]);

  const formatIST = (utcStr) => {
    if (!utcStr) return null;
    try {
      return new Date(utcStr).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    } catch { return null; }
  };
  const formattedTime = formatIST(alertTime);

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ style: { borderRadius: 20, overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.25)" } }}
    >
      {/* ── Header ── */}
      <div style={{ background: "#082D6B", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-40%", right: "-5%", width: 200, height: 200, background: "radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />

        {
          label != "Lock Location" &&
          <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0, background: `${borderColor}25`, border: `1.5px solid ${borderColor}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ transform: "scale(0.75)", display: "flex" }}>{alertConfig.icon}</div>
          </div>

        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{alertTitle}</span>
            <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", padding: "2px 8px", borderRadius: 20, background: `${borderColor}30`, border: `1px solid ${borderColor}60`, color: "#fff", whiteSpace: "nowrap" }}>
              {notificationType?.replace(/_/g, " ") || ""}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {vehiclePart && (
              <span style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>{vehiclePart}</span>
            )}
            {formattedTime && (
              <>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>•</span>
                <span style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", display: "flex", alignItems: "center", gap: 3 }}>
                  🕐 {formattedTime}
                </span>
              </>
            )}
            {hasValidCoords && tooltipClasses && (
              <>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>•</span>
                <Tooltip
                  title={<><strong style={{ display: "block", marginBottom: "4px" }}>Address</strong><span style={{ fontWeight: 500, opacity: 0.9 }}>{address || "Fetching address..."}</span></>}
                  interactive
                  classes={{ tooltip: tooltipClasses.customTooltip, popper: tooltipClasses.tooltipPopper }}
                  placement="top" arrow
                >
                  <span style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", maxWidth: 200, display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}>
                    {address || "Fetching address..."}
                  </span>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.35)", color: "#f87171", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700 }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.30)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
        >✕</button>
      </div>

      <div style={{ height: 3, background: borderColor, opacity: 0.85 }} />

      {/* ── Map ── */}
      <div style={{ height: 430, position: "relative" }}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={center}
            zoom={hasValidCoords ? 15 : 5}
            options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: true, zoomControl: true }}
          >
            {hasValidCoords && customIcon && <Marker position={center} icon={customIcon} zIndex={1000} />}
            {hasValidCoords && !customIcon && (
              <Marker position={center} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: borderColor, fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2.5 }} />
            )}
          </GoogleMap>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", gap: 10, color: "rgba(8,45,107,0.4)", fontSize: 13, fontWeight: 600 }}>
            <CircularProgress size={18} style={{ color: "#082D6B" }} /> Loading map…
          </div>
        )}
        {!hasValidCoords && isLoaded && (
          <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.65)", color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 600, backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}>
            No coordinates — showing default location
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{ background: "#f8fafc", borderTop: "1px solid #e8eeff", padding: "10px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>
          {hasValidCoords ? `📍 ${parsedLat.toFixed(6)}, ${parsedLng.toFixed(6)}` : "📍 Coordinates unavailable"}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {address && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(address);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              title="Copy address"
              style={{
                background: copied ? "#f0fdf4" : "#fff",
                color: copied ? "#16a34a" : "#64748b",
                border: `1px solid ${copied ? "#bbf7d0" : "#e2e8f0"}`,
                borderRadius: 8, padding: "6px 14px", fontSize: 14,
                fontWeight: 600, cursor: "pointer", display: "flex",
                alignItems: "center", gap: 5,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={e => { if (!copied) e.currentTarget.style.background = "#f1f5f9"; }}
              onMouseLeave={e => { if (!copied) e.currentTarget.style.background = "#fff"; }}
            >
              {copied ? "✅ Copied!" : "📋 Copy Address"}
            </button>
          )}
          {hasValidCoords && (
            <button
              onClick={() => window.open(`https://maps.google.com/?q=${parsedLat},${parsedLng}`, "_blank")}
              style={{ background: "#082D6B", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
              onMouseEnter={e => e.currentTarget.style.background = "#0f3d8c"}
              onMouseLeave={e => e.currentTarget.style.background = "#082D6B"}
            >
              Open in Google Maps
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default MapModal;
